// Employee-set support preferences (Vision & Strategy §5/§6 — "employee access
// to support information and preferences").
//
// These are set by the *employee themselves* in the portal — distinct from the
// manager-authored `supportPreferences` free-text on the RTW plan. They govern
// how the employee wants to be contacted and whether/how often the platform
// should prompt their manager to reach out (touch points). A touch-point
// opt-out is a *consent* signal: the employee asking us not to nudge their
// manager to check in. See `shouldDispatchTouchpoint` for the behavioural gate.

import { z } from 'zod';
import {
  ALWAYS_DISPATCH_TOUCHPOINT_TYPES,
  EARLY_TOUCHPOINT_TYPES,
  type TouchpointType,
} from './cases.ts';

// How the employee wants to receive direct platform communication. `none` =
// the employee opts out of direct platform emails/messages entirely.
export const CONTACT_METHODS = ['email', 'workspace', 'none'] as const;
export type ContactMethod = (typeof CONTACT_METHODS)[number];

// Touch-point cadence. `default` = full 12-month series; `lighter` = drop the
// high-frequency early touch points (Day 3 / Week 1), keep the milestones;
// `none` = no touch-point reminders at all (a full opt-out, equivalent to
// `touchpointOptIn: false`).
export const TOUCHPOINT_CADENCES = ['default', 'lighter', 'none'] as const;
export type TouchpointCadence = (typeof TOUCHPOINT_CADENCES)[number];

// ---------------------------------------------------------------------------
// Employee Portal & App spec §11.1 — communication preference options.
// ---------------------------------------------------------------------------
//
// These are spec-aligned, employee-set choices about how the *organisation*
// (a human — manager or HR) should reach out. They are deliberately distinct
// from `preferredContactMethod` above, which governs the *platform's own*
// delivery channel (does besideU itself email/message you). Every field is
// OPTIONAL/nullable: the spec is emphatic that the product must not assume the
// employee has decided (§3.3, §11). `null` = "not set", and the UI must render
// that as an un-chosen state, never a fabricated default. The `unsure` /
// `decide_later` enum members let the employee record an explicit non-decision.

// §11.1 Preferred contact person.
export const CONTACT_PERSONS = ['manager', 'hr', 'either', 'unsure'] as const;
export type ContactPerson = (typeof CONTACT_PERSONS)[number];

// §11.1 Preferred contact method (how a human should reach out). Distinct from
// `CONTACT_METHODS` (platform delivery channel). `none` = "no contact for now".
export const CONTACT_CHANNELS = ['email', 'phone', 'teams', 'text', 'in_person', 'none'] as const;
export type ContactChannel = (typeof CONTACT_CHANNELS)[number];

// §11.1 Contact frequency.
export const CONTACT_FREQUENCIES = [
  'as_planned',
  'less_often',
  'only_if_necessary',
  'employee_initiates',
] as const;
export type ContactFrequency = (typeof CONTACT_FREQUENCIES)[number];

// §11.1 / §17 Wider-team communication.
export const WIDER_TEAM_COMMS = [
  'do_not_tell',
  'share_limited',
  'ask_me_first',
  'already_agreed',
] as const;
export type WiderTeamComms = (typeof WIDER_TEAM_COMMS)[number];

// §11.1 / §17 Colleague contact.
export const COLLEAGUE_CONTACTS = ['okay', 'close_only', 'none_for_now'] as const;
export type ColleagueContact = (typeof COLLEAGUE_CONTACTS)[number];

// §11.1 Return-to-work discussion (the lightweight signal that lives with the
// comms prefs; the richer §16.2 return-to-work group is below).
export const RTW_DISCUSSIONS = ['ready', 'not_yet', 'hr_only', 'manager_only'] as const;
export type RtwDiscussion = (typeof RTW_DISCUSSIONS)[number];

// §11.1 Key-date awareness.
export const KEY_DATE_AWARENESS = ['aware_ok', 'prefer_not_record', 'decide_later'] as const;
export type KeyDateAwareness = (typeof KEY_DATE_AWARENESS)[number];

// §17 Team communicator — who should tell the wider team, if anyone.
export const TEAM_COMMUNICATORS = ['manager', 'hr', 'either', 'unsure'] as const;
export type TeamCommunicator = (typeof TEAM_COMMUNICATORS)[number];

// ---------------------------------------------------------------------------
// §16.2 — Employee return-to-work preferences (return-specific group).
// ---------------------------------------------------------------------------
// Distinct from the manager-authored RTW plan and from the §11.1 lightweight
// `rtwDiscussion` above: these are the employee's own return choices.
export const RTW_READINESS = ['yes', 'not_yet', 'unsure'] as const;
export type RtwReadiness = (typeof RTW_READINESS)[number];

export const RTW_CONTACTS = ['manager', 'hr', 'either'] as const;
export type RtwContact = (typeof RTW_CONTACTS)[number];

export const RTW_TEAM_AWARENESS = ['tell_team', 'limited', 'do_not_tell_yet'] as const;
export type RtwTeamAwareness = (typeof RTW_TEAM_AWARENESS)[number];

export const RTW_FIRST_DAY = ['quiet_return', 'normal_routine', 'manager_first', 'unsure'] as const;
export type RtwFirstDay = (typeof RTW_FIRST_DAY)[number];

// Shared yes/no/unsure tri-state (§16.2 workload concerns, flexibility needed).
export const TRISTATES = ['yes', 'no', 'unsure'] as const;
export type Tristate = (typeof TRISTATES)[number];

// The spec-aligned comms/RTW/team preference groups, all nullable. Reused by
// both the request schema (employee write) and the stored/returned shape.
const specPreferenceFields = {
  // §11.1 communication preferences
  preferredContactPerson: z.enum(CONTACT_PERSONS).nullable(),
  preferredContactChannel: z.enum(CONTACT_CHANNELS).nullable(),
  contactFrequency: z.enum(CONTACT_FREQUENCIES).nullable(),
  widerTeamComms: z.enum(WIDER_TEAM_COMMS).nullable(),
  colleagueContact: z.enum(COLLEAGUE_CONTACTS).nullable(),
  rtwDiscussion: z.enum(RTW_DISCUSSIONS).nullable(),
  keyDateAwareness: z.enum(KEY_DATE_AWARENESS).nullable(),
  // §17 team communication
  shareRelationshipWithTeam: z.boolean().nullable(),
  teamCommunicator: z.enum(TEAM_COMMUNICATORS).nullable(),
  // §16.2 return-to-work
  rtwReadiness: z.enum(RTW_READINESS).nullable(),
  rtwContact: z.enum(RTW_CONTACTS).nullable(),
  rtwTeamAwareness: z.enum(RTW_TEAM_AWARENESS).nullable(),
  rtwFirstDay: z.enum(RTW_FIRST_DAY).nullable(),
  rtwWorkloadConcerns: z.enum(TRISTATES).nullable(),
  rtwFlexibilityNeeded: z.enum(TRISTATES).nullable(),
  rtwHrSupportRequested: z.boolean().nullable(),
} as const;

export const SetSupportPreferencesRequestSchema = z
  .object({
    preferredContactMethod: z.enum(CONTACT_METHODS),
    touchpointOptIn: z.boolean(),
    touchpointCadence: z.enum(TOUCHPOINT_CADENCES),
    ...specPreferenceFields,
  })
  // New spec fields are optional on the wire so an older client that only sends
  // the original three keys still validates; absent → left unchanged by the
  // repo upsert (it coalesces undefined to the stored value).
  .partial({
    preferredContactPerson: true,
    preferredContactChannel: true,
    contactFrequency: true,
    widerTeamComms: true,
    colleagueContact: true,
    rtwDiscussion: true,
    keyDateAwareness: true,
    shareRelationshipWithTeam: true,
    teamCommunicator: true,
    rtwReadiness: true,
    rtwContact: true,
    rtwTeamAwareness: true,
    rtwFirstDay: true,
    rtwWorkloadConcerns: true,
    rtwFlexibilityNeeded: true,
    rtwHrSupportRequested: true,
  })
  .strict();
export type SetSupportPreferencesRequest = z.infer<typeof SetSupportPreferencesRequestSchema>;

// The full spec preference group, all nullable (null = employee has not chosen).
export type SpecPreferenceFields = {
  [K in keyof typeof specPreferenceFields]: z.infer<(typeof specPreferenceFields)[K]>;
};

export interface EmployeeSupportPreferences extends SpecPreferenceFields {
  preferredContactMethod: ContactMethod;
  touchpointOptIn: boolean;
  touchpointCadence: TouchpointCadence;
  // ISO-8601; null when the employee has never set preferences (defaults apply).
  updatedAt: string | null;
}

// Every spec field defaults to null — the employee has decided nothing yet.
const NULL_SPEC_PREFERENCES: SpecPreferenceFields = {
  preferredContactPerson: null,
  preferredContactChannel: null,
  contactFrequency: null,
  widerTeamComms: null,
  colleagueContact: null,
  rtwDiscussion: null,
  keyDateAwareness: null,
  shareRelationshipWithTeam: null,
  teamCommunicator: null,
  rtwReadiness: null,
  rtwContact: null,
  rtwTeamAwareness: null,
  rtwFirstDay: null,
  rtwWorkloadConcerns: null,
  rtwHrSupportRequested: null,
  rtwFlexibilityNeeded: null,
};

// The platform default applied when an employee has never set preferences:
// full series, contactable by email, and no spec choice yet recorded. Mirrors
// the DB column defaults so the portal renders a sensible starting state.
export const DEFAULT_SUPPORT_PREFERENCES: EmployeeSupportPreferences = {
  preferredContactMethod: 'email',
  touchpointOptIn: true,
  touchpointCadence: 'default',
  ...NULL_SPEC_PREFERENCES,
  updatedAt: null,
};

// The §11/§16/§17 spec preference keys — the domain of the §11.2 visibility
// matrix. Single source of truth (the repo + the role-visibility filter both
// read it) so they can never diverge.
export const SPEC_PREFERENCE_KEYS = Object.keys(
  NULL_SPEC_PREFERENCES,
) as (keyof SpecPreferenceFields)[];

// Pull just the spec fields out of any source (a stored row, the portal
// payload), coalescing absent values to null.
export function pickSpecPreferences(source: Partial<SpecPreferenceFields>): SpecPreferenceFields {
  return Object.fromEntries(
    SPEC_PREFERENCE_KEYS.map((k) => [k, source[k] ?? null]),
  ) as SpecPreferenceFields;
}

// Consent gate for the touch-point reminder sweep. Pure + unit-testable.
//
//   - No preference set → every touch point fires (default behaviour).
//   - Operational return-to-work touch points (Pre_Return / Week_1_Back) are
//     manager-side return-to-work prep, NOT grief check-in pressure on the
//     employee — they always fire regardless of preference.
//   - Hard opt-out (`touchpointOptIn: false` or cadence `none`) → suppress
//     every grief-cadence touch-point reminder. NB since Slice 3 this includes
//     the RTW-anchored months (`Month_1/3/6/9`): they are grief outreach that
//     respects consent, NOT operational logistics, so they are gated here.
//   - `lighter` cadence → suppress only the early high-frequency touch points.
export function shouldDispatchTouchpoint(
  type: TouchpointType,
  pref:
    | Pick<EmployeeSupportPreferences, 'touchpointOptIn' | 'touchpointCadence'>
    | null
    | undefined,
): boolean {
  if (!pref) return true;
  if ((ALWAYS_DISPATCH_TOUCHPOINT_TYPES as readonly string[]).includes(type)) return true;
  if (!pref.touchpointOptIn || pref.touchpointCadence === 'none') return false;
  if (
    pref.touchpointCadence === 'lighter' &&
    (EARLY_TOUCHPOINT_TYPES as readonly string[]).includes(type)
  ) {
    return false;
  }
  return true;
}
